import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import {
  GLOBAL_SHORTCUTS,
  SHORTCUTS_OVERLAY_CHORD,
  SHORTCUTS_OVERLAY_DEF,
  ShortcutCategory,
} from '../../config/accessibilityConfig';
import { SHORTCUTS } from '../../services/keyboardShortcutService';
import './KeyboardShortcutsOverlay.css';

const CATEGORY_ORDER: ShortcutCategory[] = ['Navigation', 'Editor', 'Accessibility'];

export const KeyboardShortcutsOverlay: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  /**
   * S132: the dialog is assembled from the two catalogues that have handlers
   * behind them, rather than from a third hand-written list.
   *
   * - Navigation and Accessibility come from `GLOBAL_SHORTCUTS`, every entry of
   *   which `GlobalShortcuts` dispatches.
   * - Editor comes from the canonical `SHORTCUTS`, filtered to `implemented` —
   *   the ids something calls `registerShortcut` for. The catalogue also holds
   *   Ctrl+F, Ctrl+P, F5 and five others that nothing has ever bound; listing
   *   them was the old defect in a different file.
   */
  const groupedShortcuts = useMemo(() => {
    const groups: Record<ShortcutCategory, Array<[string, string]>> = {
      Navigation: [],
      Editor: [],
      Accessibility: [],
    };

    Object.entries(GLOBAL_SHORTCUTS).forEach(([key, def]) => {
      groups[def.category].push([key, def.description]);
    });
    groups.Accessibility.push([SHORTCUTS_OVERLAY_CHORD, SHORTCUTS_OVERLAY_DEF.description]);

    SHORTCUTS.filter((s) => s.implemented).forEach((s) => {
      groups.Editor.push([s.key, s.description]);
    });

    return groups;
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Focus goes back where it came from — WCAG 2.4.3. Without this a
    // keyboard-only child is returned to the top of the document.
    openerRef.current?.focus();
  }, []);

  const shortcuts = useMemo(
    () => ({ [SHORTCUTS_OVERLAY_CHORD]: () => setIsOpen((open) => !open) }),
    []
  );

  useKeyboardShortcuts(shortcuts);

  // The footer promises Escape closes the dialog, and nothing listened for it:
  // `useKeyboardShortcuts` is not given an Escape entry, and no handler existed
  // here. A child who opened the dialog with the keyboard could not close it
  // with the keyboard.
  useEffect(() => {
    if (!isOpen) return undefined;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  return (
    <>
      <button
        ref={openerRef}
        className="keyboard-shortcuts-button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Show keyboard shortcuts"
        aria-expanded={isOpen}
        title="Press ? for keyboard shortcuts"
      >
        ⌨️
      </button>

      {isOpen && (
        <div
          className="keyboard-shortcuts-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shortcuts-title"
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="keyboard-shortcuts-content">
            <div className="keyboard-shortcuts-header">
              <h2 id="shortcuts-title">Keyboard Shortcuts</h2>
              <button className="close-button" onClick={close} aria-label="Close keyboard shortcuts">
                ✕
              </button>
            </div>

            <div className="keyboard-shortcuts-body">
              {CATEGORY_ORDER.map((category) => (
                <div key={category} className="shortcut-category">
                  <h3>{category}</h3>
                  <ul className="shortcuts-list">
                    {groupedShortcuts[category].map(([key, description]) => (
                      <li key={`${category}-${key}`} className="shortcut-item">
                        <kbd className="shortcut-key">{key}</kbd>
                        <span className="shortcut-description">{description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="keyboard-shortcuts-footer">
              <p>Press ESC or click the X to close this dialog.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
